import { useRef, useEffect, useLayoutEffect } from 'react'

import gsap from 'gsap'

export type AnimationType = 'fade' | 'slideUp' | 'slideDown' | 'slideLeft' | 'slideRight'

interface UseTemplateAnimationOptions {
  animationType?: AnimationType
  duration?: number
  delay?: number
}

/**
 * Hook for animating template components with GSAP
 * GPU-accelerated via transform and opacity properties
 *
 * @param isVisible - Whether the component should be visible
 * @param options - Animation configuration options
 * @returns Ref to attach to the animated container element
 */
export const useTemplateAnimation = (isVisible: boolean, options: UseTemplateAnimationOptions = {}) => {
  const { animationType = 'fade', duration = 0.5, delay = 0 } = options

  const ref = useRef<HTMLDivElement>(null)
  const tl = useRef<gsap.core.Timeline | null>(null)
  const hasAnimated = useRef(false)

  // Use useLayoutEffect to set initial state BEFORE browser paint
  // This prevents the flash of content in final position
  // Skip if already animated in — allows animationType to change mid-display
  // (e.g. scorebug repositioning) without hiding the element
  useLayoutEffect(() => {
    if (!ref.current || hasAnimated.current) return

    // Kill any in-progress tweens on this element before setting initial state.
    // Without this, an old reverse animation can override our set() value on
    // the next GSAP tick, causing the entrance to animate from the wrong position.
    gsap.killTweensOf(ref.current)

    // Set initial hidden state immediately (before paint)
    if (animationType === 'fade') {
      gsap.set(ref.current, { opacity: 0 })
    } else if (animationType === 'slideUp') {
      gsap.set(ref.current, { y: '100%', opacity: 0 })
    } else if (animationType === 'slideDown') {
      gsap.set(ref.current, { y: '-100%', opacity: 0 })
    } else if (animationType === 'slideLeft') {
      gsap.set(ref.current, { x: '100%', opacity: 0 })
    } else if (animationType === 'slideRight') {
      gsap.set(ref.current, { x: '-100%', opacity: 0 })
    }
  }, [animationType])

  // Create timeline after initial state is set
  // When animationType changes while visible, rebuilds timeline so exit
  // direction matches the current position (e.g. slideDown exit for top positions)
  useEffect(() => {
    if (!ref.current) return

    // Kill existing timeline if it exists
    if (tl.current) {
      tl.current.kill()
      tl.current = null
    }

    tl.current = gsap.timeline({ paused: true })

    // Define animation based on type
    if (animationType === 'fade') {
      tl.current.to(ref.current, {
        opacity: 1,
        duration,
        delay,
        ease: 'power2.out',
      })
    } else if (animationType === 'slideUp') {
      tl.current.to(ref.current, {
        y: '0%',
        opacity: 1,
        duration,
        delay,
        ease: 'power2.out',
      })
    } else if (animationType === 'slideDown') {
      tl.current.to(ref.current, {
        y: '0%',
        opacity: 1,
        duration,
        delay,
        ease: 'power2.out',
      })
    } else if (animationType === 'slideLeft') {
      tl.current.to(ref.current, {
        x: '0%',
        opacity: 1,
        duration,
        delay,
        ease: 'power2.out',
      })
    } else if (animationType === 'slideRight') {
      tl.current.to(ref.current, {
        x: '0%',
        opacity: 1,
        duration,
        delay,
        ease: 'power2.out',
      })
    }

    // If already visible, seek timeline to end so reverse works correctly
    if (hasAnimated.current) {
      tl.current.progress(1, false)
    }

    return () => {
      if (tl.current) {
        tl.current.kill()
        tl.current = null
      }
    }
  }, [animationType, duration, delay])

  useEffect(() => {
    if (!tl.current) return

    if (isVisible) {
      tl.current.play()
      hasAnimated.current = true
    } else if (hasAnimated.current) {
      // Only reverse if we've already animated in
      tl.current.reverse()
      // Reset after exit completes so re-entry can set fresh initial state
      hasAnimated.current = false
    }
  }, [isVisible, animationType])

  return ref
}

export default useTemplateAnimation
